/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then, When } from "react-if";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FlexContainer, SVGIcon, Text, theme, Tooltip } from "@nordcloud/gnui";
import { useCurrency } from "~/hooks";
import { formatMoney } from "~/tools";

const DEFAULT_REASON =
  "This resource is in a non-running mode for more than 180 days and is considered a waste.";

type Amount = {
  amount: string;
  uri?: string;
};

type Props = {
  reason?: string;
  amount?: Amount;
  size?: "lg" | "md" | "sm" | "xl" | "xxl";
  liftIcon?: boolean;
};

export function WasteIndicator({
  reason = DEFAULT_REASON,
  amount,
  size = "md",
  liftIcon = false,
}: Props) {
  const { currency } = useCurrency();
  const money = formatMoney(amount?.amount as string, currency);
  const marginTop = liftIcon ? `-${theme.spacing.spacing03}` : 0;

  return (
    <div css={{ position: "relative", display: "flex", cursor: "pointer" }}>
      <Tooltip caption={reason} minWidth="15rem">
        <FlexContainer alignItems="center" justifyContent="center">
          <SVGIcon
            name="cloudWaste"
            size={size}
            color="success"
            css={{
              marginLeft: theme.spacing.spacing03,
              marginRight: theme.spacing.spacing01,
              marginTop,
            }}
          />
          <When condition={amount !== undefined}>
            <Text size={size} weight="medium" color="success" m={0}>
              <If condition={amount?.uri !== undefined}>
                <Then>
                  <WasteLink to={amount?.uri || ""}>{money}</WasteLink>
                </Then>
                <Else>{money}</Else>
              </If>
            </Text>
          </When>
        </FlexContainer>
      </Tooltip>
    </div>
  );
}

const WasteLink = styled(Link)`
  color: ${theme.colors.success};

  &:hover {
    color: ${theme.colors.success};
  }
`;
